import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Folder,
  File,
  Plus,
  Search,
  Loader2,
  MoreVertical,
  FolderPlus,
  Upload,
  Trash2,
  Share2,
  Lock,
  Globe,
  Users,
  ArrowLeft,
  Download,
  Edit,
  Move,
  FileText,
  Image as ImageIcon,
  Video,
  ChevronRight,
  Home,
  X,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Files = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  
  // Dialog states
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [uploadFileOpen, setUploadFileOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  
  // Form states
  const [creating, setCreating] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null); // 'file' or 'folder'
  const [shareUsers, setShareUsers] = useState([]);
  const [searchUsers, setSearchUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [allFolders, setAllFolders] = useState([]);
  
  const [newFolder, setNewFolder] = useState({
    name: '',
    access_level: 'private'
  });
  
  const [newFile, setNewFile] = useState({
    name: '',
    file_url: '',
    file_type: 'document',
    description: '',
    access_level: 'private'
  });
  
  const [editItem, setEditItem] = useState({
    name: '',
    access_level: '',
    description: ''
  });
  
  const [fileVotes, setFileVotes] = useState({}); // {fileId: 'up' | 'down' | null}

  const folderId = searchParams.get('folder');

  useEffect(() => {
    fetchContents();
    if (folderId) {
      fetchBreadcrumbs(folderId);
    } else {
      setBreadcrumbs([]);
      setCurrentFolder(null);
    }
  }, [folderId]);

  const fetchContents = async () => {
    setLoading(true);
    try {
      const [foldersRes, filesRes] = await Promise.all([
        axios.get(`${API}/folders${folderId ? `?parent_id=${folderId}` : ''}`),
        axios.get(`${API}/files${folderId ? `?folder_id=${folderId}` : ''}`)
      ]);
      setFolders(foldersRes.data);
      setFiles(filesRes.data);
    } catch (error) {
      console.error('Error fetching contents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBreadcrumbs = async (folderId) => {
    try {
      const crumbs = [];
      let currentId = folderId;
      
      while (currentId) {
        const response = await axios.get(`${API}/folders/${currentId}`);
        crumbs.unshift(response.data);
        currentId = response.data.parent_id;
      }
      
      setBreadcrumbs(crumbs);
      if (crumbs.length > 0) {
        setCurrentFolder(crumbs[crumbs.length - 1]);
      }
    } catch (error) {
      console.error('Error fetching breadcrumbs:', error);
    }
  };

  const fetchAllFolders = async () => {
    try {
      const response = await axios.get(`${API}/folders`);
      setAllFolders(response.data.filter(f => f.owner_id === user.id));
    } catch (error) {
      console.error('Error fetching all folders:', error);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolder.name.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/folders`, {
        ...newFolder,
        parent_id: folderId || null
      });
      setFolders([...folders, response.data]);
      setNewFolder({ name: '', access_level: 'private' });
      setCreateFolderOpen(false);
      toast.success('Folder created!');
    } catch (error) {
      toast.error('Failed to create folder');
    } finally {
      setCreating(false);
    }
  };

  const handleUploadFile = async () => {
    if (!newFile.name.trim() || !newFile.file_url.trim()) {
      toast.error('Please fill in required fields');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/files`, {
        ...newFile,
        folder_id: folderId || null
      });
      setFiles([...files, response.data]);
      setNewFile({
        name: '',
        file_url: '',
        file_type: 'document',
        description: '',
        access_level: 'private'
      });
      setUploadFileOpen(false);
      toast.success('File added!');
    } catch (error) {
      toast.error('Failed to add file');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedType === 'folder') {
        await axios.delete(`${API}/folders/${selectedItem.id}`);
        setFolders(folders.filter(f => f.id !== selectedItem.id));
      } else {
        await axios.delete(`${API}/files/${selectedItem.id}`);
        setFiles(files.filter(f => f.id !== selectedItem.id));
      }
      toast.success(`${selectedType === 'folder' ? 'Folder' : 'File'} deleted`);
      setDeleteOpen(false);
      setSelectedItem(null);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = async () => {
    try {
      if (selectedType === 'folder') {
        await axios.put(`${API}/folders/${selectedItem.id}`, {
          name: editItem.name,
          access_level: editItem.access_level
        });
        setFolders(folders.map(f => 
          f.id === selectedItem.id ? { ...f, ...editItem } : f
        ));
      } else {
        await axios.put(`${API}/files/${selectedItem.id}`, editItem);
        setFiles(files.map(f => 
          f.id === selectedItem.id ? { ...f, ...editItem } : f
        ));
      }
      toast.success('Updated successfully');
      setEditOpen(false);
      setSelectedItem(null);
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSearchUsers = async (query) => {
    setUserSearchQuery(query);
    if (query.length < 2) {
      setSearchUsers([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/search/users?q=${query}`);
      setSearchUsers(response.data.filter(u => 
        u.id !== user.id && !shareUsers.find(su => su.id === u.id)
      ));
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleShare = async () => {
    if (shareUsers.length === 0) {
      toast.error('Please select users to share with');
      return;
    }
    try {
      const endpoint = selectedType === 'folder' ? 'folders' : 'files';
      await axios.post(`${API}/${endpoint}/${selectedItem.id}/share`, {
        user_ids: shareUsers.map(u => u.id)
      });
      toast.success('Shared successfully!');
      setShareOpen(false);
      setShareUsers([]);
      setSelectedItem(null);
      fetchContents();
    } catch (error) {
      toast.error('Failed to share');
    }
  };

  const handleMove = async (targetFolderId) => {
    try {
      await axios.post(`${API}/files/${selectedItem.id}/move${targetFolderId ? `?folder_id=${targetFolderId}` : ''}`);
      setFiles(files.filter(f => f.id !== selectedItem.id));
      toast.success('File moved!');
      setMoveOpen(false);
      setSelectedItem(null);
    } catch (error) {
      toast.error('Failed to move file');
    }
  };

  const openFolder = (folder) => {
    setSearchParams({ folder: folder.id });
  };

  const goToRoot = () => {
    setSearchParams({});
  };

  const goToFolder = (folder) => {
    setSearchParams({ folder: folder.id });
  };

  const getAccessIcon = (level) => {
    switch (level) {
      case 'public': return <Globe className="w-3 h-3 text-success" />;
      case 'shared': return <Users className="w-3 h-3 text-info" />;
      default: return <Lock className="w-3 h-3 text-muted-foreground" />;
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'image': return <ImageIcon className="w-8 h-8 text-secondary" />;
      case 'video': return <Video className="w-8 h-8 text-primary" />;
      default: return <FileText className="w-8 h-8 text-muted-foreground" />;
    }
  };

  const openItemMenu = (item, type) => {
    setSelectedItem(item);
    setSelectedType(type);
    setEditItem({
      name: item.name,
      access_level: item.access_level,
      description: item.description || ''
    });
  };

  const handleFileVote = async (fileId, voteType, e) => {
    e.stopPropagation();
    try {
      const response = await axios.post(`${API}/files/${fileId}/vote`, { vote_type: voteType });
      const newVote = response.data.vote;
      
      setFileVotes(prev => ({ ...prev, [fileId]: newVote }));
      
      // Update file score in UI
      setFiles(files.map(f => {
        if (f.id !== fileId) return f;
        
        const oldVote = fileVotes[fileId];
        let scoreChange = 0;
        
        if (oldVote === null || oldVote === undefined) {
          scoreChange = newVote === 'up' ? 1 : newVote === 'down' ? -1 : 0;
        } else if (newVote === null) {
          scoreChange = oldVote === 'up' ? -1 : 1;
        } else {
          scoreChange = newVote === 'up' ? 2 : -2;
        }
        
        return { ...f, score: (f.score || 0) + scoreChange };
      }));
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const filteredFolders = folders.filter(f => 
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const filteredFiles = files.filter(f => 
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-4xl font-heading font-medium mb-2">My Files</h1>
            <p className="text-muted-foreground">
              Organize and manage your documents securely
            </p>
          </div>

          <div className="flex gap-2">
            <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="create-folder-btn">
                  <FolderPlus className="w-4 h-4 mr-2" />
                  New Folder
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">Create Folder</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Folder Name</Label>
                    <Input
                      placeholder="My Documents"
                      value={newFolder.name}
                      onChange={(e) => setNewFolder({ ...newFolder, name: e.target.value })}
                      data-testid="folder-name-input"
                      className="input-field"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Access Level</Label>
                    <Select
                      value={newFolder.access_level}
                      onValueChange={(val) => setNewFolder({ ...newFolder, access_level: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private">
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4" /> Private
                          </div>
                        </SelectItem>
                        <SelectItem value="public">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4" /> Public
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    className="w-full btn-primary"
                    onClick={handleCreateFolder}
                    disabled={creating}
                    data-testid="submit-folder-btn"
                  >
                    {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Create Folder
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={uploadFileOpen} onOpenChange={setUploadFileOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="upload-file-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Add File
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">Add File</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>File Name *</Label>
                    <Input
                      placeholder="document.pdf"
                      value={newFile.name}
                      onChange={(e) => setNewFile({ ...newFile, name: e.target.value })}
                      data-testid="file-name-input"
                      className="input-field"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>File URL *</Label>
                    <Input
                      placeholder="https://..."
                      value={newFile.file_url}
                      onChange={(e) => setNewFile({ ...newFile, file_url: e.target.value })}
                      data-testid="file-url-input"
                      className="input-field"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>File Type</Label>
                      <Select
                        value={newFile.file_type}
                        onValueChange={(val) => setNewFile({ ...newFile, file_type: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="document">Document</SelectItem>
                          <SelectItem value="image">Image</SelectItem>
                          <SelectItem value="video">Video</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Access</Label>
                      <Select
                        value={newFile.access_level}
                        onValueChange={(val) => setNewFile({ ...newFile, access_level: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="private">Private</SelectItem>
                          <SelectItem value="public">Public</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      placeholder="Optional description..."
                      value={newFile.description}
                      onChange={(e) => setNewFile({ ...newFile, description: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <Button 
                    className="w-full btn-primary"
                    onClick={handleUploadFile}
                    disabled={creating}
                    data-testid="submit-file-btn"
                  >
                    {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Add File
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 mb-6 text-sm">
          <button 
            onClick={goToRoot}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="breadcrumb-root"
          >
            <Home className="w-4 h-4" />
            My Files
          </button>
          {breadcrumbs.map((crumb, idx) => (
            <React.Fragment key={crumb.id}>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
              <button
                onClick={() => goToFolder(crumb)}
                className={`hover:text-foreground transition-colors ${
                  idx === breadcrumbs.length - 1 ? 'text-foreground font-medium' : 'text-muted-foreground'
                }`}
              >
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search files and folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 input-field max-w-md"
            data-testid="files-search"
          />
        </div>

        {/* Content */}
        {filteredFolders.length === 0 && filteredFiles.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <Folder className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl mb-2">
                {searchQuery ? 'No results found' : 'This folder is empty'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? 'Try a different search term' : 'Create folders or add files to get started'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Folders */}
            {filteredFolders.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">Folders</h2>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredFolders.map((folder) => (
                    <Card 
                      key={folder.id}
                      className="card-interactive cursor-pointer group"
                      data-testid={`folder-${folder.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div 
                            className="flex items-center gap-3 flex-1 min-w-0"
                            onClick={() => openFolder(folder)}
                          >
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <Folder className="w-6 h-6 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-medium truncate">{folder.name}</h3>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {getAccessIcon(folder.access_level)}
                                <span className="capitalize">{folder.access_level}</span>
                              </div>
                            </div>
                          </div>
                          
                          {folder.owner_id === user?.id && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openItemMenu(folder, 'folder');
                                  }}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                                  openItemMenu(folder, 'folder');
                                  setShareOpen(true);
                                }}>
                                  <Share2 className="w-4 h-4 mr-2" />
                                  Share
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  openItemMenu(folder, 'folder');
                                  setEditOpen(true);
                                }}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-destructive"
                                  onClick={() => {
                                    openItemMenu(folder, 'folder');
                                    setDeleteOpen(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Files */}
            {filteredFiles.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">Files</h2>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredFiles.map((file) => (
                    <Card 
                      key={file.id}
                      className="card-interactive group"
                      data-testid={`file-${file.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center shrink-0">
                              {getFileIcon(file.file_type)}
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-medium truncate">{file.name}</h3>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {getAccessIcon(file.access_level)}
                                <span className="capitalize">{file.file_type}</span>
                              </div>
                            </div>
                          </div>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => openItemMenu(file, 'file')}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => window.open(file.file_url, '_blank')}>
                                <Download className="w-4 h-4 mr-2" />
                                Open/Download
                              </DropdownMenuItem>
                              {file.owner_id === user?.id && (
                                <>
                                  <DropdownMenuItem onClick={() => {
                                    openItemMenu(file, 'file');
                                    setShareOpen(true);
                                  }}>
                                    <Share2 className="w-4 h-4 mr-2" />
                                    Share
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    openItemMenu(file, 'file');
                                    setEditOpen(true);
                                  }}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    openItemMenu(file, 'file');
                                    fetchAllFolders();
                                    setMoveOpen(true);
                                  }}>
                                    <Move className="w-4 h-4 mr-2" />
                                    Move
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    className="text-destructive"
                                    onClick={() => {
                                      openItemMenu(file, 'file');
                                      setDeleteOpen(true);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        
                        {file.description && (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                            {file.description}
                          </p>
                        )}
                        
                        {/* Voting for public/shared files */}
                        {file.access_level !== 'private' && (
                          <div className="flex items-center gap-2 mt-3 pt-2 border-t">
                            <button
                              className={`p-1 rounded transition-colors ${
                                fileVotes[file.id] === 'up' 
                                  ? 'text-primary bg-primary/10' 
                                  : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
                              }`}
                              onClick={(e) => handleFileVote(file.id, 'up', e)}
                              data-testid={`file-upvote-${file.id}`}
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <span className={`text-xs font-medium ${
                              (file.score || 0) > 0 ? 'text-primary' : 
                              (file.score || 0) < 0 ? 'text-destructive' : 'text-muted-foreground'
                            }`}>
                              {file.score || 0}
                            </span>
                            <button
                              className={`p-1 rounded transition-colors ${
                                fileVotes[file.id] === 'down' 
                                  ? 'text-destructive bg-destructive/10' 
                                  : 'text-muted-foreground hover:text-destructive hover:bg-destructive/5'
                              }`}
                              onClick={(e) => handleFileVote(file.id, 'down', e)}
                              data-testid={`file-downvote-${file.id}`}
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Share Dialog */}
        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">
                Share {selectedType === 'folder' ? 'Folder' : 'File'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Search Users</Label>
                <Input
                  placeholder="Search by username..."
                  value={userSearchQuery}
                  onChange={(e) => handleSearchUsers(e.target.value)}
                  className="input-field"
                />
              </div>
              
              {searchUsers.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {searchUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => {
                        setShareUsers([...shareUsers, u]);
                        setSearchUsers(searchUsers.filter(su => su.id !== u.id));
                        setUserSearchQuery('');
                      }}
                      className="w-full flex items-center gap-2 p-2 rounded hover:bg-muted text-left"
                    >
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={u.avatar_url} />
                        <AvatarFallback>{u.display_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">@{u.username}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {shareUsers.length > 0 && (
                <div className="space-y-2">
                  <Label>Sharing with:</Label>
                  <div className="flex flex-wrap gap-2">
                    {shareUsers.map((u) => (
                      <Badge key={u.id} variant="secondary" className="flex items-center gap-1">
                        @{u.username}
                        <button onClick={() => setShareUsers(shareUsers.filter(su => su.id !== u.id))}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <Button className="w-full btn-primary" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">
                Edit {selectedType === 'folder' ? 'Folder' : 'File'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editItem.name}
                  onChange={(e) => setEditItem({ ...editItem, name: e.target.value })}
                  className="input-field"
                />
              </div>
              <div className="space-y-2">
                <Label>Access Level</Label>
                <Select
                  value={editItem.access_level}
                  onValueChange={(val) => setEditItem({ ...editItem, access_level: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="shared">Shared</SelectItem>
                    <SelectItem value="public">Public</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {selectedType === 'file' && (
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={editItem.description}
                    onChange={(e) => setEditItem({ ...editItem, description: e.target.value })}
                    rows={2}
                  />
                </div>
              )}
              <Button className="w-full btn-primary" onClick={handleEdit}>
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Move Dialog */}
        <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Move File</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">Select destination folder:</p>
              <div className="max-h-64 overflow-y-auto space-y-1">
                <button
                  onClick={() => handleMove(null)}
                  className="w-full flex items-center gap-2 p-3 rounded-lg hover:bg-muted text-left"
                >
                  <Home className="w-5 h-5 text-muted-foreground" />
                  <span>Root (My Files)</span>
                </button>
                {allFolders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => handleMove(folder.id)}
                    className="w-full flex items-center gap-2 p-3 rounded-lg hover:bg-muted text-left"
                    disabled={folder.id === selectedItem?.folder_id}
                  >
                    <Folder className="w-5 h-5 text-primary" />
                    <span>{folder.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this {selectedType}
                {selectedType === 'folder' && ' and all its contents'}.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default Files;
